import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import ReactHtmlParser from 'react-html-parser';

import { PrimaryButton, ProButton } from '@components/Button/variants';
import LazyImgixImage from '@components/LazyImgixImage';
import { useAppContext } from '@contexts/AppContext';
import { useBlueconicContext } from '@contexts/BlueconicContext';
import useEventTracking from '@hooks/useEventTracking';
import useOffers from '@hooks/useOffers';
import useWindowSize from '@hooks/useWindowSize';
import { Theme } from '@interfaces/global';
import { ClickTarget, click } from '@tracking';
import { captureBlueconicClickEvent } from '@utils/blueconic/utils';
import { WIDTH_LG, WIDTH_MD } from '@utils/constants';
import { formatNumber } from '@utils/formatting';
import getSubscriptionPriceBreakdown from '@utils/getSubscriptionPriceBreakdown';

import LeadCaptureModalBody from '../LeadCaptureModalBody';
import { LeadCaptureModalBodyProps } from '../LeadCaptureModalBody';
import { ModalHeaderProps } from '../common/modalHeader';

export type LeadCaptureModalSplitImgProps = ModalHeaderProps & LeadCaptureModalBodyProps;

const LeadCaptureModalSplitImg = ({
  buttonText,
  imgPathLarge,
  imgPathMedium,
  imgPathSmall,
  leadButtonText = 'Subscribe Now',
  leadType,
  offerCode,
  offerLink,
  subtitle,
  title,
}: LeadCaptureModalSplitImgProps) => {
  const { data: offersData, isLoading: isLoadingOffer } = useOffers({ offer_codes: [offerCode] });
  const { initialCurrentUser } = useAppContext();
  const { trackEvent } = useEventTracking();
  const { width: windowWidth } = useWindowSize();
  const [currentImagePath, setCurrentImagePath] = useState<string | null>(null);
  const { variantId } = useBlueconicContext();

  useEffect(() => {
    if (windowWidth >= WIDTH_LG) {
      setCurrentImagePath(imgPathLarge);
    } else if (windowWidth >= WIDTH_MD) {
      setCurrentImagePath(imgPathMedium);
    } else {
      setCurrentImagePath(imgPathSmall);
    }
  }, [windowWidth, imgPathLarge, imgPathMedium, imgPathSmall]);

  const isCurrentUserPresented = !!initialCurrentUser;

  if (isLoadingOffer || !offersData || offersData.length === 0) {
    return null;
  }

  const offerData = offersData[0];

  const {
    billingFrequencyOverride,
    currentPrice,
    currentPriceDisplayOverride,
    description,
    isPro,
    isBestValue,
    originalPrice,
    originalPriceDisplayOverride,
    title: offerTitle,
  } = offerData;

  const { currentPriceToDisplay, hasDiscount, originalPriceToDisplay } =
    getSubscriptionPriceBreakdown({
      currentPrice,
      currentPriceDisplayOverride,
      originalPrice,
      originalPriceDisplayOverride,
    });
  const Button = isPro ? ProButton : PrimaryButton;
  const textColor = isPro ? 'text-pro-gold' : 'text-primary-magenta';

  const clickTargetCapture = () => {
    trackEvent(click({ clickTarget: ClickTarget.Subscribe, clickText: buttonText }));
    captureBlueconicClickEvent(variantId);
  };

  return (
    <aside className="flex min-h-full w-full flex-wrap md:min-h-[420px]">
      <div className="flex w-full items-center px-24 py-24 md:w-1/2 md:pl-36">
        <div className="lg:max-w-[420px]">
          <div className="flex flex-col items-start">
            {isBestValue && (
              <span className={clsx('font-suisse-intl text-overline-md', textColor)}>
                Best Value
              </span>
            )}
            <h2
              className={clsx(
                'm-0 font-suisse-works text-headline-sm font-normal text-black sm:text-headline-lg',
                textColor
              )}
            >
              {title || offerTitle}
            </h2>
          </div>
          <div className="mt-8 flex flex-wrap items-baseline">
            <span className="font-suisse-screen text-headline-sm text-black sm:text-headline-lg">
              ${formatNumber(currentPriceToDisplay)}
            </span>
            {hasDiscount && (
              <span className="ml-8 font-suisse-screen text-headline-sm text-grayscale-gray line-through sm:text-headline-lg">
                ${formatNumber(originalPriceToDisplay)}
              </span>
            )}
            {billingFrequencyOverride && (
              <span className="ml-8 font-suisse-screen text-headline-xxs text-grayscale-charcoal">
                {billingFrequencyOverride}
              </span>
            )}
          </div>
          <div className="mt-8 min-h-72 font-suisse-intl text-body-md text-grayscale-gray md:min-h-56 md:text-body-lg">
            {subtitle || ReactHtmlParser(description)}
          </div>
          {isCurrentUserPresented ? (
            <Button
              className="mt-32 sm:mt-0 md:mt-32"
              as="link"
              href={offerLink}
              onClickCapture={clickTargetCapture}
            >
              {buttonText}
            </Button>
          ) : (
            <div className="mt-32 w-full max-w-[404px] sm:mt-0 md:mt-32">
              <LeadCaptureModalBody
                buttonText={leadButtonText}
                leadType={leadType}
                theme={Theme.Light}
                offerLink={offerLink}
                withOauth={false}
                isPro={isPro}
              />
            </div>
          )}
        </div>
      </div>
      <div className="w-full md:w-1/2">
        {currentImagePath && (
          <LazyImgixImage
            src={currentImagePath}
            className="h-full w-full"
            role="presentation"
            wrapperClassName="flex-shrink-0 h-full w-full"
          />
        )}
      </div>
    </aside>
  );
};

export default LeadCaptureModalSplitImg;
